/**
 * SegmentFillUnified — u6-segment-fill.
 *
 * ONE "Segment Fill" section for the segment side pane: the color/design of the
 * Segment FRAME. It folds the previously-separate stand-alone "Segment Color"
 * accordion together with the fill-type editor into a single accordion whose
 * type tabs are Color / Gradient / Pattern / Image / GIF — exactly the Edit-
 * Wheel-Flow deck (design-handoff `ewflow-segment-fills`, states 75x).
 *
 * RE-HOUSING, NOT FORKING: it mounts the EXISTING controls unchanged —
 *   · Color   → the app's rich <SegmentColor> palette (Preset / Custom / Wheel /
 *               Segment), injected into the panel's Color tab via the additive
 *               `colorPanel` prop. Writes the legacy `settings.color` field via
 *               the same useSegmentSettings().handleChange("segmentColor") path
 *               the stand-alone section used — the wedge colour behaviour is
 *               byte-for-byte the same.
 *   · Gradient/Pattern/Image/GIF → the WL-012 <SegmentFillPanel> pickers +
 *               FocalZoomControl and <GradientBuilderPanel>, writing the
 *               `settings.fill` model (+ the legacy currentBg/bgWallpaper fields
 *               via legacyFieldsForFill, + the snake_case model fields when
 *               SEGMENT_ARC_FILLS is ON) through the same undoable
 *               updateSegmentSetting dispatch SegmentStylingPane uses.
 *
 * No data model changes: this is presentation only. The Wallpaper section (the
 * open-Segment CANVAS background) is a separate section and is untouched.
 *
 * Gated by SEGMENT_FILL_UNIFIED_ENABLED at the call site (SegmentSettings): OFF
 * renders nothing here and the stand-alone "Segment Color" accordion shows as
 * today; ON renders this section and hides the stand-alone one.
 */

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateSegmentSetting } from "../../../../redux/reducers/segmentBuilder";
import { withHistory } from "../../../../redux/actions/history";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { SEGMENT_ARC_FILLS_ENABLED } from "../../../D3Wheels/segmentArcFillsFlag";
import { defaultSegmentFill } from "../SegmentStyling/logic/segmentFill";
import { legacyFieldsForFill } from "../SegmentStyling/logic/legacyFillFields";
import { fillToModelFields, resolveSegmentFill } from "../SegmentStyling/logic/fillModelFeed";
import SegmentFillPanel from "../SegmentStyling/SegmentFillPanel";
import GradientBuilderPanel from "../SegmentStyling/GradientBuilderPanel";
import SegmentColor from "../../../SegmentSettings/children/SegmentColor";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToolAccordion from "../ToolAccordion";
import useSegmentSettings from "../../../../hooks/useSegmentSetting";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";
// Part A (flag REACT_APP_SEGMENT_PALETTE_COLOR): when a non-color fill is
// committed, (re)generate the Segment Palette FROM THE FILL so the Segment Color
// can harmonize with it. Flag OFF -> untouched.
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../SegmentStyling/logic/segmentPaletteColorFlag";
// D18 (flag REACT_APP_SEG_PALETTE_AUTODERIVE): also re-derive the Segment Palette
// from the fill media, debounced, with an Undo-toast safety net.
import { SEG_PALETTE_AUTODERIVE } from "../../../../constants/featureFlags";
import {
  useBrokenMediaFocus,
  segmentFillFocus,
  markBrokenMediaResolved,
} from "../BrokenMedia/brokenMediaFocus";

// T22.355: the file name of a broken fill URL, for the notice ("which image?").
function brokenFileName(url) {
  if (!url || typeof url !== "string") return "";
  const path = url.split(/[?#]/)[0];
  try {
    return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
  } catch (e) {
    return path.substring(path.lastIndexOf("/") + 1);
  }
}

const SegmentFillUnified = ({ segment: segmentProp } = {}) => {
  const dispatch = useDispatch();
  const selected = useSelector(getSelectedSegment);
  const segment = segmentProp || selected;
  const { handleChange } = useSegmentSettings();
  const {
    fetchSegmentPalette,
    fetchSegmentPaletteFromFill,
    fetchSegmentPaletteFromFillDebounced,
  } = useSegmentPalettes();
  const brokenFocus = useBrokenMediaFocus();

  if (!segment) return null;

  // T22.355: the broken-media "Open" row for THIS segment's fill. Opens the
  // section and says what is broken, instead of a collapsed "Segment Fill"
  // with nothing flagged.
  const brokenFill = segmentFillFocus(brokenFocus, segment.id);

  // The `settings.fill` model (WL-012) — present only while the in-memory
  // redux state still holds whatever the user just picked THIS session.
  //
  // stgfix-1008-NEW-EditWheel-3 (image-fill-opens-color-tab, live-reproduced
  // wheel 507615 seg 6871694): `ActivitySettings` (yuga_wheel/models.py) has NO
  // `fill` column at all — only the LEGACY render fields (`current_bg`,
  // `bg_wallpaper`, `segment_bg_color`) actually persist. So after a reload,
  // `GET /api/v2/wheel/<id>/` NEVER returns a `settings.fill` key for ANY
  // segment — measured live: an Image fill saved, reloaded, still paints
  // correctly on the wedge (current_bg=2, bg_wallpaper=<url>), but this read
  // fell straight through to `defaultSegmentFill(segment.color)` (type
  // "color"), so the panel opened on the Color tab, the Image tab showed an
  // empty picker, and clicking it flagged "Unsaved changes" over a fill the
  // user never touched. `resolveSegmentFill` (fillModelFeed.js) already
  // reconstructs an image/gif/pattern model from those same legacy fields for
  // the WEDGE renderer (SegmentView, WheelComponent) — this panel just never
  // called it. Order matters: an in-session `settings.fill` (any type,
  // including gradient/color, which the resolver does not reconstruct) still
  // wins first, so live editing behaviour is unchanged; the resolver only
  // fills the gap once that blob is absent.
  const fill =
    (segment.settings && segment.settings.fill) ||
    resolveSegmentFill(segment.settings) ||
    defaultSegmentFill(segment.color);

  // The segment's CURRENT colour. A pick in the injected <SegmentColor> is
  // written to `settings.color` (useSegmentSetting "segmentColor"), which the
  // wedge paints from; top-level `segment.color` keeps the value the wheel was
  // LOADED with. T22.181 (FE#7033 review): reading only `segment.color` made the
  // preview stay on the old colour and Color -> Gradient seed the gradient from
  // it (pick red, switch to Gradient -> the load-time green gradient).
  const segmentColor =
    (segment.settings && segment.settings.color) || segment.color;

  // Keep the panel's small live preview honest for the Color tab: show the
  // segment's actual wedge colour (which the injected <SegmentColor> edits)
  // rather than a stale/default model colour. No model write — display only.
  const displayFill =
    fill.type === "color"
      ? { ...fill, color: segmentColor || fill.color }
      : fill;

  // ONE undoable dispatch: the V2 fill blob + the legacy render fields
  // (image/gif/pattern) + the additive snake_case model fields when the arc-
  // fills flag is ON. Identical to SegmentStylingPane.commitFill (reuse, not
  // fork) — color/gradient contribute no legacy fields, so those paths are
  // byte-identical.
  const commitFill = (nextFill) => {
    const data = {
      fill: nextFill,
      ...legacyFieldsForFill(nextFill),
      ...(SEGMENT_ARC_FILLS_ENABLED ? fillToModelFields(nextFill) : {}),
    };
    dispatch(withHistory(updateSegmentSetting({ id: segment.id, data })));
    // T22.355: any new fill replaces the broken image -- drop the row.
    if (brokenFill) markBrokenMediaResolved(brokenFill.id);
    // Part A: re-seed the Segment Palette FROM the newly-committed fill (image/
    // gif via the Palette Generator extractor; gradient/pattern via their
    // colors). Color fills route back to the color path inside the hook. Flag
    // OFF -> not called.
    if (
      (SEGMENT_PALETTE_COLOR_ENABLED || SEG_PALETTE_AUTODERIVE) &&
      segment.id != null
    ) {
      const nextSettings = { ...(segment.settings || {}), ...data };
      // D18 on -> debounced (collapse rapid media swaps) + Undo-toast safety net,
      // inside the hook. D18 off -> the original immediate call, byte-identical.
      if (SEG_PALETTE_AUTODERIVE) {
        fetchSegmentPaletteFromFillDebounced(nextSettings, segment.id);
      } else {
        fetchSegmentPaletteFromFill(nextSettings, segment.id);
      }
    }
  };

  const commitGradient = (nextGradient) =>
    commitFill({ ...fill, type: "gradient", gradient: nextGradient });

  // The Color tab re-houses the stand-alone "Segment Color" control verbatim:
  // write the legacy `settings.color` field + refresh the derived palette,
  // exactly as SegmentSettings did before this merge.
  const onSegmentColor = (color) => {
    handleChange("segmentColor", null, color);
    if (segment.id != null) fetchSegmentPalette(color, segment.id);
  };

  const removeBrokenFill = () =>
    commitFill(defaultSegmentFill(segmentColor));

  return (
    <ToolAccordion
      title="Segment Fill"
      hideDivider
      RightIconComponent={ExpandMoreIcon}
      expandSignal={brokenFill ? brokenFill.id : null}
      summaryAdornment={
        brokenFill ? (
          <span
            data-testid="segment-fill-broken-chip"
            style={{
              marginLeft: 8,
              padding: "2px 8px",
              borderRadius: 100,
              background: "#FDECEA",
              color: "#C62828",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Broken
          </span>
        ) : null
      }
    >
      {brokenFill && (
        <div
          role="alert"
          data-testid="segment-fill-broken-notice"
          ref={(el) => {
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ block: "nearest" });
            }
          }}
          style={{
            margin: "0 0 12px",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #F5C2C0",
            background: "#FDECEA",
            color: "#7A1C1C",
            fontSize: 13,
            lineHeight: "18px",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            This Segment&apos;s fill image can&apos;t be loaded
          </div>
          {brokenFileName(brokenFill.url) && (
            <div style={{ wordBreak: "break-all", marginBottom: 6 }}>
              {brokenFileName(brokenFill.url)}
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            Pick a new Image or GIF below, or remove the broken image.
          </div>
          <button
            type="button"
            data-testid="segment-fill-broken-remove"
            onClick={removeBrokenFill}
            style={{
              border: "1px solid #C62828",
              background: "#ffffff",
              color: "#C62828",
              borderRadius: 100,
              padding: "4px 12px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Remove broken image
          </button>
        </div>
      )}
      <SegmentFillPanel
        fill={displayFill}
        onChange={commitFill}
        segmentName={segment.title || segment.name || "Segment"}
        colorPanel={
          <SegmentColor
            showTabs={false}
            showSuggestedColorPalette={false}
            currentColor={segmentColor}
            onColorChange={onSegmentColor}
          />
        }
      />
      {fill.type === "gradient" && (
        <GradientBuilderPanel
          gradient={fill.gradient}
          onChange={commitGradient}
        />
      )}
    </ToolAccordion>
  );
};

export default SegmentFillUnified;
